import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteHits } from 'react-instantsearch';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconFilter } from '@sorare/core/src/atoms/icons/system/iconFilter.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import type { Algolia_CardHit_anyCard } from '@sorare/core/src/lib/__generated__/algolia.graphql.ts';
import type {
  CardHit,
  CardHit as CardHitType,
} from '@sorare/core/src/lib/algolia';
import {
  createCardSorts,
  getUserCardFilters,
} from '@sorare/core/src/lib/algolia';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import type { TradeCardsSearchDocument } from '#src/hooks/search/useBackendTradeCardsSearch.ts';
import { FiltersManager } from '#src/search/FiltersManager/index.tsx';
import { FiltersManagerDialog } from '#src/search/FiltersManagerDialog/index.tsx';
import { SearchBox } from '#src/search/SearchBox/index.tsx';
import { SortCards } from '#src/searchCards/SortCards/index.tsx';
import { tradeFilters as FootballTradeFilters } from '#src/searchCards/football/cardFilters.tsx';
import { tradeFilters as MLBTradeFilters } from '#src/searchCards/mlb/cardFilters.tsx';
import { tradeFilters as NBATradeFilters } from '#src/searchCards/nba/cardFilters.tsx';

import {
  BackendCardPickerContent,
  supportsBackendTradesPicker,
} from './BackendCardPickerContent.tsx';
import type { CardSelectionProps } from './CardPickerResults.tsx';
import { CardPickerResults, FilterColumn, Row } from './CardPickerResults.tsx';
import type { CardPicker_anyCard } from './__generated__/CardPickerResults.graphql.ts';

type Props<DATA extends CardPicker_anyCard & Algolia_CardHit_anyCard> = {
  owner: { id: string; slug: string };
  searchQuery: TradeCardsSearchDocument<DATA>;
  onClose: () => void;
  title: MessageDescriptor;
  selectedCards: CardHitType[];
  confirmSelectedCards: (cards: CardHitType[]) => void;
  counterOfferSport?: Sport;
  maxCards?: number;
  fetchMissingCardsData: (
    newState: CardHit[],
    knownCards?: Record<string, DATA>
  ) => Promise<boolean>;
  cardsData: Record<string, CardPicker_anyCard>;
};

const CardPicker = ({
  selectedCards,
  setSelectedCards,
  maxCards,
  fetchMissingCardsData,
  cardsData,
}: CardSelectionProps) => {
  const [searching, setSearching] = useState<boolean>(false);

  const {
    items,
    results: algoliaResults,
    isLastPage,
    showMore,
  } = useInfiniteHits<CardHitType>();

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setSearching(false);
  }, [algoliaResults?.page]);

  return (
    <CardPickerResults
      items={items}
      hasMore={!isLastPage}
      searching={searching}
      onLoadMore={() => {
        setSearching(true);
        showMore();
      }}
      selectedCards={selectedCards}
      setSelectedCards={setSelectedCards}
      maxCards={maxCards}
      fetchMissingCardsData={fetchMissingCardsData}
      cardsData={cardsData}
    />
  );
};

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 var(--triple-unit);
  height: 100%;
  @media ${tabletAndAbove} {
    container-name: dialog-content;
    container-type: normal;
    max-height: 70vh;
  }
`;
const CtaWrapper = styled.div`
  position: sticky;
  bottom: 0;
  box-shadow: 0px 14px 50px rgba(0, 0, 0, 0.2);
  padding: var(--triple-unit);
`;

export const CardPickerDialog = <
  DATA extends CardPicker_anyCard & Algolia_CardHit_anyCard,
>({
  onClose,
  owner,
  searchQuery,
  title,
  selectedCards: defaultSelectedCards,
  confirmSelectedCards,
  counterOfferSport,
  maxCards,
  fetchMissingCardsData,
  cardsData,
}: Props<DATA>) => {
  const { isMe } = useCurrentUserContext();
  const {
    flags: { enableGraphqlTradesPicker = false },
  } = useFeatureFlags();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const isLaptop = useIsLaptop();
  const [selectedCards, setSelectedCards] =
    useState<CardHitType[]>(defaultSelectedCards);
  const onConfirmCb = useCallback(
    () => confirmSelectedCards(selectedCards),
    [confirmSelectedCards, selectedCards]
  );
  const { sport } = useSportContext();

  const filters = useMemo(
    () => [getUserCardFilters(idFromObject(owner.id))],
    [owner.id]
  );

  const tradeSport = counterOfferSport || sport;

  const useBackendSearch =
    !!enableGraphqlTradesPicker && supportsBackendTradesPicker(tradeSport);

  const tradeFiltersBySport = {
    [Sport.FOOTBALL]: FootballTradeFilters,
    [Sport.BASEBALL]: MLBTradeFilters,
    [Sport.NBA]: NBATradeFilters,
  };

  const tradeSearchFilters = tradeFiltersBySport[tradeSport];

  const cardSorts = createCardSorts({
    withHighestAverageScore: true,
    withHighestPrice: false,
    withEndingSoon: false,
    withNew: true,
    withBestValue: true,
    withLowestPrice: false,
  });

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      onBack={onClose}
      onClose={onClose}
      title={
        <BodyM className="text-center">
          <FormattedMessage {...title} />
        </BodyM>
      }
      footer={
        <CtaWrapper>
          <Button color="primary" size="medium" onClick={onConfirmCb} fullWidth>
            <FormattedMessage {...glossary.done} />
          </Button>
        </CtaWrapper>
      }
    >
      <DialogContent>
        <CurrentMarketplaceUserProvider
          sport={tradeSport}
          withMyBidsBanner={false}
        >
          <SearchCardsContextProvider
            commonMarket={false}
            {...(isMe(owner) ? {} : { galleryOwnerSlug: owner.slug })}
          >
            {useBackendSearch ? (
              <BackendCardPickerContent
                owner={owner}
                searchQuery={searchQuery}
                tradeSport={tradeSport}
                selectedCards={selectedCards}
                setSelectedCards={setSelectedCards}
                maxCards={maxCards}
                fetchMissingCardsData={fetchMissingCardsData}
                cardsData={cardsData}
              />
            ) : (
              <InstantCardSearch
                indexes={[cardSorts[0]]}
                analyticsTags={['NewOffer']}
                defaultFilters={filters}
                availableSorts={cardSorts}
                sport={tradeSport}
                defaultHitsPerPage={15}
                attributesToRetrieve={['*']}
              >
                <Vertical gap={2}>
                  <Horizontal>
                    <SearchBox />
                    {!!isLaptop && <SortCards indexes={cardSorts} />}
                    {!isLaptop && (
                      <IconButton
                        size="small"
                        color="quaternary"
                        icon={iconFilter}
                        onClick={() => setFilterDialogOpen(true)}
                      />
                    )}
                  </Horizontal>

                  <Row gap={2}>
                    {isLaptop ? (
                      <FilterColumn>
                        <FiltersManager filters={tradeSearchFilters} />
                      </FilterColumn>
                    ) : (
                      <FiltersManagerDialog
                        open={filterDialogOpen}
                        onClose={() => setFilterDialogOpen(false)}
                        filtersGroups={tradeSearchFilters}
                      />
                    )}
                    <CardPicker
                      selectedCards={selectedCards}
                      setSelectedCards={setSelectedCards}
                      maxCards={maxCards}
                      cardsData={cardsData}
                      fetchMissingCardsData={fetchMissingCardsData}
                    />
                  </Row>
                </Vertical>
              </InstantCardSearch>
            )}
          </SearchCardsContextProvider>
        </CurrentMarketplaceUserProvider>
      </DialogContent>
    </Dialog>
  );
};

CardPickerDialog.fragments = CardPickerResults.fragments;
